/* ==========================================================================
   WAENN — Vídeo "ambiente" con sonido
   ==========================================================================
   Restricción real de todos los navegadores (Chrome, Safari, Firefox): un
   <video autoplay> SOLO puede arrancar solo si empieza silenciado (muted).
   Autoplay con sonido desde el primer instante, sin ninguna interacción
   previa de la persona, no es posible en ningún navegador moderno -- no es
   una limitación de este código, es una politica de plataforma pensada
   para que las webs no puedan reproducir sonido sin permiso.

   Lo mas parecido a "que suene siempre" que se puede conseguir de forma
   fiable:
     1) Intentar reproducir CON sonido nada mas cargar (algunos navegadores
        lo permiten si el dominio ya tiene "Media Engagement Index" alto
        por visitas anteriores).
     2) Si el navegador lo bloquea (lo habitual en una visita nueva), cae
        automáticamente a reproducir en bucle SIN sonido.
     3) En cuanto la persona interactúa una sola vez con la pagina -- un
        clic, un toque, una tecla, o el primer scroll --, se quita el
        silencio automáticamente. En la practica esto ocurre en los
        primeros segundos de casi cualquier visita real. (Nota: un
        <video> YA reproduciéndose puede pasar de muted a sonido sin
        necesitar un gesto "fuerte" del usuario -- la política de autoplay
        solo bloquea EMPEZAR a reproducir con sonido, no cambiar el
        atributo muted de un vídeo que ya está en marcha -- por eso basta
        con el primer scroll para desbloquear el audio, aunque el scroll
        en sí nunca cuenta como gesto válido para INICIAR una reproducción
        con sonido.)

   El audio (y la reproducción) dependen de si el vídeo está realmente
   visible en pantalla, usando un IntersectionObserver (nunca un listener
   de scroll continuo ni un temporizador):
     - Visible (>= 50% dentro del viewport): el vídeo se reproduce y, si el
       sonido ya fue desbloqueado alguna vez (autoplay con sonido aceptado
       o una interacción previa), se activa el audio automáticamente, sin
       pedir permiso de nuevo.
     - No visible, o casi no visible (< 50%): el audio se silencia al
       instante -- antes incluso de pausar el vídeo, para que no se
       escuche ni una fracción de segundo de más -- y el vídeo se pausa
       (ahorra batería/CPU; al volver a ser visible retoma desde el mismo
       fotograma, sin reiniciar ni parpadear).
   Como el <video> nunca se destruye entre secciones dentro de la misma
   página (no hay navegación SPA en este tema -- cada página es una carga
   completa nueva), cambiar de página o entrar en una colección detiene el
   audio automáticamente sin necesitar ningún código adicional: el vídeo
   deja de existir en el DOM al cargar la nueva página.

   Capa extra de seguridad: además del IntersectionObserver, se reafirma el
   estado correcto (mute/play acorde a la última visibilidad conocida) cada
   vez que la pestaña vuelve a primer plano (visibilitychange), cada vez
   que la ventana recupera el foco o la página se restaura desde el cache
   de retroceso (pageshow/focus), cada vez que termina la transición CSS
   del scroll "por secciones" de la portada (transitionend sobre
   "transform") y en el frame de pintado siguiente a cualquier gesto que
   pueda cambiar de sección (click, teclado, rueda, swipe). Ninguno de
   estos es un temporizador ni un listener de scroll continuo: son eventos
   puntuales del navegador (incluido un unico requestAnimationFrame por
   gesto, no un bucle) que sirven de red de seguridad por si, en un
   instante muy concreto, el navegador retrasara la entrega de un aviso
   del IntersectionObserver o saltara la sección de un salto sin animar.
   ========================================================================== */

(function () {
  'use strict';

  var VISIBLE_THRESHOLD = 0.5;

  function getVideos() {
    return Array.prototype.slice.call(document.querySelectorAll('.waenn-ambient-video__el'));
  }

  // Se pone a "true" en cuanto el navegador demuestra que permite sonido en
  // el vídeo (autoplay con sonido aceptado, o tras la primera interacción
  // de la persona). A partir de ahí, el IntersectionObserver puede activar
  // el audio automáticamente cada vez que el vídeo vuelva a ser visible,
  // sin volver a pedir permiso.
  var soundUnlocked = false;

  // Último estado de visibilidad conocido de cada vídeo, actualizado por el
  // IntersectionObserver. Se reutiliza también al volver de una pestaña en
  // segundo plano o al recuperar el foco.
  var visibilityState = new WeakMap();

  // Guarda la promesa devuelta por el último play() de cada vídeo. Es
  // necesario porque, si se llama a pause() mientras esa promesa todavía
  // está pendiente (vídeo con conexión lenta, sin datos cargados todavía),
  // el navegador puede ignorar esa pausa y el vídeo se queda reproduciendo
  // en cuanto el play() termine de resolver -- un problema real y conocido
  // ("play() request was interrupted by a call to pause()"), no solo
  // teórico. safePause espera a que esa promesa se resuelva antes de
  // pausar de verdad, así la pausa SIEMPRE se aplica, sin importar en qué
  // punto de la carga esté el vídeo.
  var pendingPlay = new WeakMap();

  function safePlay(video) {
    var playPromise = video.play();
    pendingPlay.set(video, playPromise);
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        /* Reproducción bloqueada en este instante: no hay nada más que
           hacer automáticamente -- se reintentará en la próxima
           interacción o cambio de visibilidad. */
      });
    }
  }

  function safePause(video) {
    var playPromise = pendingPlay.get(video);
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () { video.pause(); }).catch(function () {});
    } else {
      video.pause();
    }
  }

  function tryUnmutedAutoplay(video) {
    video.muted = false;
    var playPromise = video.play();
    pendingPlay.set(video, playPromise);
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise
        .then(function () {
          soundUnlocked = true;
        })
        .catch(function () {
          // El navegador bloqueo el autoplay con sonido: vuelve a intentar
          // en silencio (esto casi nunca falla).
          video.muted = true;
          safePlay(video);
        });
    } else {
      soundUnlocked = true;
    }
  }

  function unmuteAllOnFirstInteraction() {
    soundUnlocked = true;
    getVideos().forEach(function (video) {
      // Si el vídeo ya salió de pantalla antes de la primera interacción
      // (poco probable, pero posible), no lo desmuteamos a ciegas -- deja
      // que sea el IntersectionObserver el que decida en cuanto vuelva a
      // ser visible.
      if (visibilityState.get(video) !== false) {
        video.muted = false;
        if (video.paused) safePlay(video);
      }
    });
  }

  // Aplica el estado correcto (mute + play/pause) a un vídeo según la
  // última visibilidad conocida. Se usa tanto desde el propio
  // IntersectionObserver como desde las capas de seguridad adicionales
  // (visibilitychange, focus, pageshow, transitionend, requestAnimationFrame
  // tras un gesto), así que toda la lógica de "qué hacer" vive en un
  // único sitio.
  function applyVisibilityState(video, visible) {
    if (visible) {
      if (soundUnlocked) video.muted = false;
      if (video.paused) safePlay(video);
    } else {
      // Silencio inmediato, siempre lo primero -- antes de pausar -- para
      // que el corte de audio sea instantáneo.
      video.muted = true;
      if (!video.paused) safePause(video);
    }
  }

  var observer = null;

  function setupVisibilityObserver(videos) {
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      // Sin soporte (extremadamente raro hoy en día): se asume visible
      // siempre, en vez de romper la reproducción existente.
      videos.forEach(function (video) {
        visibilityState.set(video, true);
      });
      return;
    }

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          var visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_THRESHOLD;
          visibilityState.set(video, visible);
          applyVisibilityState(video, visible);
        });
      },
      { threshold: [0, 0.25, VISIBLE_THRESHOLD, 0.75, 1] }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  // Vuelve a comprobar la posición REAL de cada vídeo en el viewport ahora
  // mismo (getBoundingClientRect, sin esperar al próximo aviso asíncrono
  // del observer) y aplica el estado que corresponda. Sirve de red de
  // seguridad puntual -- no es un bucle ni un temporizador, solo se llama
  // desde manejadores de eventos concretos y de una sola vez cada vez.
  function resyncNow() {
    var videos = getVideos();
    if (!videos.length) return;

    videos.forEach(function (video) {
      var rect = video.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      var visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      var visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      var area = rect.width * rect.height;
      var visibleRatio = area > 0 ? (visibleHeight * visibleWidth) / area : 0;
      var visible = visibleRatio >= VISIBLE_THRESHOLD;

      visibilityState.set(video, visible);
      applyVisibilityState(video, visible);
    });
  }

  // Segunda capa de protección: si la persona cambia de pestaña o minimiza
  // el navegador, el audio tampoco debe sonar en segundo plano aunque el
  // vídeo siga técnicamente "visible" en su propio scroll. Al volver a la
  // pestaña se reafirma según la posición real actual -- nunca se fuerza
  // la reproducción si mientras tanto el vídeo quedó fuera de pantalla.
  function handleTabVisibilityChange() {
    if (document.hidden) {
      getVideos().forEach(function (video) {
        video.muted = true;
        if (!video.paused) safePause(video);
      });
    } else {
      resyncNow();
    }
  }

  function init() {
    var videos = getVideos();
    if (!videos.length) return;

    videos.forEach(tryUnmutedAutoplay);
    setupVisibilityObserver(videos);
    document.addEventListener('visibilitychange', handleTabVisibilityChange);
    // Red de seguridad puntual (no polling): al recuperar el foco de la
    // ventana o al restaurar la página desde el cache de retroceso,
    // reafirma el estado real -- cubre el caso extremadamente raro en el
    // que el hilo principal estuviera saturado justo cuando cambió la
    // visibilidad del vídeo y el aviso del IntersectionObserver se
    // retrasara.
    window.addEventListener('focus', resyncNow);
    window.addEventListener('pageshow', resyncNow);
    // El scroll "por secciones" a pantalla completa de la portada
    // (assets/section-scroll-snap.js) mueve el carril con una transicion
    // CSS sobre "transform" (translateY), no con scroll nativo -- por eso
    // este modulo nunca engancha su propio listener de scroll. En su
    // lugar, se escucha el final de ESA transicion (transitionend, un
    // evento puntual del navegador, no un temporizador) para reafirmar de
    // inmediato la visibilidad real en cuanto el carril termina de
    // moverse, sin depender en exclusiva de la entrega asincrona del
    // IntersectionObserver.
    document.addEventListener('transitionend', function (e) {
      if (e.propertyName === 'transform') resyncNow();
    });
    // Red de seguridad adicional, tambien 100% dirigida por eventos (no un
    // temporizador ni un listener de scroll continuo): los unicos gestos
    // que pueden disparar un cambio de seccion en section-scroll-snap.js
    // son click (puntos de navegacion), teclado (flechas/Home/End), rueda
    // del raton y "touchend" (swipe). Tras cualquiera de esos gestos se
    // reafirma la visibilidad real en el siguiente frame de pintado
    // (requestAnimationFrame UNA sola vez, no un bucle) -- cubre el caso
    // en el que el navegador mueva el carril de un salto, sin animacion
    // intermedia, y por tanto sin disparar "transitionend".
    var resyncOnNextFrame = function () {
      window.requestAnimationFrame(resyncNow);
    };
    ['click', 'keydown', 'wheel', 'touchend'].forEach(function (evt) {
      window.addEventListener(evt, resyncOnNextFrame, { passive: true });
    });

    var events = ['click', 'touchstart', 'keydown', 'scroll'];
    var handler = function () {
      unmuteAllOnFirstInteraction();
      events.forEach(function (evt) {
        window.removeEventListener(evt, handler);
      });
    };
    events.forEach(function (evt) {
      window.addEventListener(evt, handler, { passive: true, once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
